/* Submission Ready badge — green ✓ when Packing List (slip OK), Bulk Data (generics OK)
 * and Bulk Labels are current with no cancelled/restored parcels pending. Batched + cached. */
import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const cache = new Map();          // key -> {ready, missing, at}
const subs = new Map();           // key -> Set(setter)
let queue = new Set();
let timer = null;
const TTL = 60000;

const flush = async () => {
  const keys = [...queue];
  queue = new Set();
  timer = null;
  if (!keys.length) return;
  try {
    const r = await axios.get(`${API}/packing-list/ready-status`, { headers: getAuthHeader(), params: { keys: keys.join(",") } });
    keys.forEach(k => {
      const v = { ...(r.data[k] || { ready: false, missing: ["Unknown"] }), at: Date.now() };
      cache.set(k, v);
      (subs.get(k) || []).forEach(fn => fn(v));
    });
  } catch { /* badge stays hidden */ }
};

const request = (k) => {
  queue.add(k);
  if (!timer) timer = setTimeout(flush, 120);
};

if (typeof window !== "undefined") {
  window.addEventListener("order-register:invoice-changed", () => {
    cache.clear();
    subs.forEach((_s, k) => request(k));
  });
}

export const invalidateReady = () => {
  cache.clear();
  subs.forEach((_s, k) => request(k));
};

export const useReady = (invoiceNo) => {
  const k = String(invoiceNo || "").trim().toUpperCase();
  const [v, setV] = useState(() => cache.get(k) || null);
  useEffect(() => {
    if (!k) return undefined;
    if (!subs.has(k)) subs.set(k, new Set());
    subs.get(k).add(setV);
    const c = cache.get(k);
    if (!c || Date.now() - c.at > TTL) request(k); else setV(c);
    return () => subs.get(k)?.delete(setV);
  }, [k]);
  return v;
};

// Submission Pack — one ZIP of the latest current documents for office submission.
const PackDownload = ({ k, testId }) => {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.stopPropagation(); e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/packing-list/submission-pack/${encodeURIComponent(k)}`, { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${k}_submission_pack.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Submission pack downloaded (${(r.headers["x-pack-contents"] || "").replaceAll(",", ", ")})`);
    } catch (err) {
      let msg = null;
      try { msg = err.response?.data instanceof Blob ? JSON.parse(await err.response.data.text()).detail : null; } catch { /* non-JSON body */ }
      toast.error(msg || "Could not build submission pack");
    } finally { setBusy(false); }
  };
  return (
    <span role="button" tabIndex={0} onClick={go} title="Download submission pack (ZIP of all current files)"
          className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-1.5 ml-0.5 cursor-pointer hover:bg-emerald-100"
          data-testid={`${testId}-pack-${k}`}>
      {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Download className="w-2.5 h-2.5" />}Pack
    </span>
  );
};

export const ReadyBadge = ({ invoiceNo, testId = "ready" }) => {
  const v = useReady(invoiceNo);
  if (!v) return null;
  const k = String(invoiceNo).trim().toUpperCase();
  return v.ready ? (
    <span className="inline-flex items-center whitespace-nowrap"><span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-white bg-emerald-600 rounded-full px-1.5"
          title="Ready for submission — Packing List (slip OK), Bulk Data and Bulk Labels are current"
          data-testid={`${testId}-ready-${k}`}>
      <CheckCircle2 className="w-2.5 h-2.5" />Ready
    </span><PackDownload k={k} testId={testId} /></span>
  ) : (
    <span className="text-[9px] font-semibold text-slate-500 bg-slate-100 border border-slate-300 rounded-full px-1.5 cursor-help"
          title={`Not ready:\n• ${v.missing.join("\n• ")}`}
          data-testid={`${testId}-not-ready-${k}`}>Not ready</span>
  );
};
